/**
 * EventRibbon component.
 *
 * A horizontal strip showing the past 2 hours of alert events.
 * Each event is a dot positioned by timestamp and colored by outcome:
 *   green  = turned away
 *   red    = entered
 *   amber  = loitering
 *   gray   = other/unknown
 *
 * Dot size scales with severity (critical=8px, warning=6px, info=4px radius).
 * Hovering shows a Radix HoverCard peek panel.
 * Clicking navigates to /event/{id}.
 *
 * Left edge shows a disabled "View timeline" link (Phase 4 scope).
 *
 * Timezone note: timestamps are displayed in the user's local timezone via
 * toLocaleTimeString(). In a future phase, the site.timezone field should be
 * used for site-local time via Intl.DateTimeFormat('en-US', { timeZone: site.timezone }).
 *
 * Design spec reference: section 3.4
 */
import { EventRibbonDot } from './EventRibbonDot';
import type { AlertSummary } from './schemas';
import * as styles from './EventRibbon.css';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  /** Alerts to display; should be past 2h, sorted by ts ASC from the server. */
  alerts: AlertSummary[];
  /** When true, shows skeleton loader instead of content. */
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * EventRibbon: horizontal event strip for the /now page bottom band.
 *
 * @param alerts - Alert summaries for the past 2 hours
 * @param isLoading - Shows skeleton when true
 */
export function EventRibbon({ alerts, isLoading }: Props) {
  // Compute time window for positioning dots
  const now = Date.now();
  const twoHoursAgo = now - 2 * 60 * 60 * 1000;
  const windowMs = now - twoHoursAgo;

  // Sort alerts by ts ascending (oldest left, newest right)
  const sortedAlerts = [...alerts].sort(
    (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime(),
  );

  /**
   * Computes the horizontal position of a dot as a percentage of the strip width.
   * Clips to [2, 98] to keep dots visually within the strip.
   */
  function getDotLeftPct(ts: string): number {
    const alertTime = new Date(ts).getTime();
    const raw = ((alertTime - twoHoursAgo) / windowMs) * 100;
    return Math.max(2, Math.min(98, raw));
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {/* Disabled "View timeline" link (Phase 4 scope) */}
        <span
          className={styles.timelineLink}
          aria-disabled="true"
          role="link"
          title="Timeline view is coming in Phase 4"
        >
          View timeline
          <span aria-hidden="true"> &rarr;</span>
        </span>

        <span
          style={{
            fontFamily: 'var(--fonts-sans)',
            fontSize: '11px',
            color: 'var(--colors-text-tertiary)',
            letterSpacing: '0.03em',
          }}
        >
          past 2h
        </span>
      </div>

      {isLoading ? (
        <div className={styles.skeleton} data-testid="ribbon-skeleton" />
      ) : alerts.length === 0 ? (
        <div className={styles.emptyState}>No activity in the past 2 hours</div>
      ) : (
        <div className={styles.strip} role="list" aria-label="Recent events">
          <div className={styles.stripFadeLeft} aria-hidden="true" />
          {sortedAlerts.map((alert) => (
            <EventRibbonDot
              key={alert.id}
              alert={alert}
              leftPct={getDotLeftPct(alert.ts)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
